import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { FinanceStatCard } from '@/modules/finance/components/FinanceStatCard';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  Download,
  Receipt,
  Clock,
  CheckCircle2,
  XCircle,
  DollarSign,
  User,
  Building2,
  Eye,
  Check,
  X,
  RefreshCw } from
'lucide-react';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger } from
'@/shared/components/ui/tabs';

const API_URL = API_BASE;

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const statusConfig = {
  pending: { label: 'Pending', class: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400', icon: Clock },
  approved: { label: 'Approved', class: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400', icon: CheckCircle2 },
  reimbursed: { label: 'Reimbursed', class: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400', icon: DollarSign },
  rejected: { label: 'Rejected', class: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-400', icon: XCircle }
};

export const ExpenseClaims = () => {const { t } = useTranslation("finance");
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchClaims = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/finance/expense-claims`;
      if (activeTab !== 'all') {
        url += `?status=${activeTab}`;
      }
      const response = await fetch(url, { headers: getAuthHeaders() });
      if (!response.ok) throw new Error('Failed to fetch claims');
      const data = await response.json();
      setClaims(data);
    } catch (err) {
      toast.error('Failed to load expense claims');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (claimId) => {
    try {
      const response = await fetch(`${API_URL}/api/finance/expense-claims/${claimId}/approve`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to approve claim');
      toast.success('Claim approved');
      fetchClaims();
    } catch (err) {
      toast.error('Failed to approve claim');
    }
  };

  useEffect(() => {
    fetchClaims();
  }, [activeTab]);

  const stats = {
    total: claims.length,
    pending: claims.filter((c) => c.status === 'pending').length,
    pendingAmount: claims.filter((c) => c.status === 'pending').reduce((sum, c) => sum + (c.amount || 0), 0),
    approved: claims.filter((c) => c.status === 'approved').length,
    reimbursed: claims.filter((c) => c.status === 'reimbursed').reduce((sum, c) => sum + (c.amount || 0), 0)
  };

  const filteredClaims = claims.filter((claim) => {
    if (!searchTerm) return true;
    return claim.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    claim.employee?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="finance-expense-claims">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("expenseClaims", "Expense Claims")}</h1>
          <p className="text-muted-foreground mt-1">{t("manageEmployeeExpense", "Manage employee expense reimbursements")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchClaims} className="gap-2">
            <RefreshCw className="h-4 w-4" />{t("refresh", "Refresh")}

          </Button>
          <Button variant="outline" className="gap-2">
            <Download className="h-4 w-4" />{t("export", "Export")}

          </Button>
          <Button className="gap-2 bg-emerald-600 hover:bg-emerald-700">
            <Plus className="h-4 w-4" />{t("newClaim", "New Claim")}

          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-4">
        <FinanceStatCard
          title="Total Claims"
          value={stats.total}
          icon={Receipt}
          color="blue" />
        
        <FinanceStatCard
          title="Pending Review"
          value={stats.pending}
          icon={Clock}
          color="gold" />
        
        <FinanceStatCard
          title="Pending Amount"
          value={stats.pendingAmount}
          icon={DollarSign}
          color="orange"
          prefix="$" />
        
        <FinanceStatCard
          title="Reimbursed"
          value={stats.reimbursed}
          icon={CheckCircle2}
          color="emerald"
          prefix="$" />
        
      </div>

      {/* Tabs and Table */}
      <Card className="card-elevated">
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="all">{t("all", "All")}</TabsTrigger>
                <TabsTrigger value="pending">{t("pending", "Pending")}</TabsTrigger>
                <TabsTrigger value="approved">{t("approved", "Approved")}</TabsTrigger>
                <TabsTrigger value="reimbursed">{t("reimbursed", "Reimbursed")}</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search claims..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10" />
              
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("claimId", "Claim ID")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("employee", "Employee")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("department", "Department")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("submitted", "Submitted")}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("amount", "Amount")}</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("items", "Items")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("status", "Status")}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filteredClaims.map((claim) => {
                  const StatusIcon = statusConfig[claim.status]?.icon || Clock;
                  return (
                    <tr key={claim.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                      <td className="py-4 px-4">
                        <span className="font-mono text-sm font-medium">{claim.id}</span>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">{claim.employee}</span>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">{claim.department}</span>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-sm">{claim.submitted}</td>
                      <td className="py-4 px-4 text-right font-mono font-medium">
                        ${claim.amount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                      <td className="py-4 px-4 text-center">
                        <Badge variant="secondary">{claim.items?.length || 0}</Badge>
                      </td>
                      <td className="py-4 px-4">
                        <Badge className={statusConfig[claim.status]?.class || statusConfig.pending.class}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {statusConfig[claim.status]?.label || claim.status}
                        </Badge>
                      </td>
                      <td className="py-4 px-4 text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost">
                            <Eye className="h-4 w-4" />
                          </Button>
                          {claim.status === 'pending' &&
                          <>
                              <Button
                              size="sm"
                              variant="ghost"
                              className="text-emerald-600 hover:text-emerald-700"
                              onClick={() => handleApprove(claim.id)}>
                              
                                <Check className="h-4 w-4" />
                              </Button>
                              <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700">
                                <X className="h-4 w-4" />
                              </Button>
                            </>
                          }
                        </div>
                      </td>
                    </tr>);

                })}
              </tbody>
            </table>
          </div>

          {filteredClaims.length === 0 &&
          <div className="py-12 text-center">
              <Receipt className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t("noClaimsFound", "No claims found")}</h3>
              <p className="text-muted-foreground">{t("tryAdjustingYour", "Try adjusting your search or filter criteria")}</p>
            </div>
          }
        </CardContent>
      </Card>
    </div>);

};